import { FAQ_CATEGORIES, NUMBERED_FAQ_ITEMS } from "@/src/features/faq/content";
import type {
  IFaqGroup,
  INumberedFaqItem,
  TFaqCategoryId,
  TFaqLang,
} from "@/src/features/faq/types";

/** Items of one category, in authoring order. */
const itemsOfCategory = (category: TFaqCategoryId): INumberedFaqItem[] =>
  NUMBERED_FAQ_ITEMS.filter((item) => item.category === category);

/**
 * The categories a teaser surface shows: its lead category first, then the rest
 * in authoring order, capped at `count`. A category with no questions is
 * skipped, so a heading never expands onto nothing.
 *
 * Labels stay localized objects rather than resolved strings, so the groups can
 * be built once at module scope and switched languages at render.
 */
const getTeaserGroups = (lead: TFaqCategoryId, count: number): IFaqGroup[] =>
  [
    ...FAQ_CATEGORIES.filter((cat) => cat.id === lead),
    ...FAQ_CATEGORIES.filter((cat) => cat.id !== lead),
  ]
    .map((cat) => ({ ...cat, items: itemsOfCategory(cat.id) }))
    .filter((group) => group.items.length > 0)
    .slice(0, count);

/**
 * Free-text search across both languages, so a Bangla query still matches an
 * English answer and vice versa.
 */
const search = (
  items: INumberedFaqItem[],
  query: string,
): INumberedFaqItem[] => {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) =>
    `${item.question.en} ${item.answer.en} ${item.question.bn} ${item.answer.bn}`
      .toLowerCase()
      .includes(q),
  );
};

/**
 * `FAQPage` structured data (schema.org). Only the canonical `/faq` page emits
 * this — repeating the same Q&A markup on the home and listing pages would be
 * duplicate structured data across URLs.
 */
const buildPageJsonLd = (
  pageUrl: string,
  lang: TFaqLang = "en",
): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${pageUrl}#faq`,
  url: pageUrl,
  inLanguage: lang === "bn" ? "bn-BD" : "en",
  mainEntity: NUMBERED_FAQ_ITEMS.map((item) => ({
    "@type": "Question",
    name: item.question[lang],
    url: `${pageUrl}#faq-${item.id}`,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer[lang],
    },
  })),
});

/** `BreadcrumbList` for Home → FAQ. */
const buildBreadcrumbJsonLd = (
  siteUrl: string,
  pageUrl: string,
  faqLabel: string,
): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
    { "@type": "ListItem", position: 2, name: faqLabel, item: pageUrl },
  ],
});

export const faqService = {
  getTeaserGroups,
  search,
  buildPageJsonLd,
  buildBreadcrumbJsonLd,
};
